"use client";

import { useState } from "react";

type Props = {
  /** `internal`: neuer Crawl aus „Internal: All“ (und optional „All Inlinks“). `links`: nur Links zu einem Crawl. */
  mode: "internal" | "links";
  busy: boolean;
  error: string | null;
  onImport: (files: { internal: File | null; links: File | null }) => void;
  onCancel: () => void;
};

const FILE = "block w-full max-w-md text-sm file:mr-3 file:h-11 file:rounded-md file:border file:border-field file:bg-white file:px-3.5";

/**
 * Import eines Screaming-Frog-Exports. Der Import ist kostenlos (die Datei wird nur gelesen, dazu ein Abruf der
 * llms.txt der eigenen Domain). Der Crawl selbst läuft in Screaming Frog.
 */
export function ImportPanel({ mode, busy, error, onImport, onCancel }: Props) {
  const [internal, setInternal] = useState<File | null>(null);
  const [links, setLinks] = useState<File | null>(null);
  const ready = mode === "internal" ? internal !== null : links !== null;

  return (
    <div role="group" aria-label="Crawl importieren" className="flex flex-col gap-3 rounded-lg border border-line bg-white px-5 py-4 text-sm">
      <h2 className="text-base font-semibold">{mode === "internal" ? "Screaming-Frog-Crawl importieren" : "All Inlinks ergänzen"}</h2>
      <p className="max-w-3xl text-[13px] text-muted">
        {mode === "internal" ? (
          <>
            Crawle die Seite in Screaming Frog und exportiere im Reiter <strong className="font-medium">Internal</strong> mit dem Filter „All“ (
            CSV oder Excel). Optional kommt <strong className="font-medium">Bulk Export › Links › All Inlinks</strong> dazu: Damit nennt das Audit auch
            die Seiten, die auf Fehlerseiten verlinken. Der Import kostet nichts, es werden nur die Dateien gelesen. Die Spalten werden über ihre
            Überschriften zugeordnet (deutsch und englisch), fehlende Spalten werden gemeldet.
          </>
        ) : (
          <>
            Exportiere in Screaming Frog <strong className="font-medium">Bulk Export › Links › All Inlinks</strong> (CSV oder Excel) zum selben Crawl. Frühere
            Links dieses Crawls werden ersetzt. Kostenlos.
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-6">
        {mode === "internal" && (
          <div className="flex flex-col gap-1">
            <label htmlFor="audit-internal" className="text-[13px] font-medium">
              Internal: All (erforderlich)
            </label>
            <input
              id="audit-internal"
              type="file"
              accept=".csv,.xlsx,text/csv"
              onChange={(event) => setInternal(event.target.files?.[0] ?? null)}
              className={FILE}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="audit-links" className="text-[13px] font-medium">
            All Inlinks {mode === "internal" ? "(optional)" : "(erforderlich)"}
          </label>
          <input
            id="audit-links"
            type="file"
            accept=".csv,.xlsx,text/csv"
            onChange={(event) => setLinks(event.target.files?.[0] ?? null)}
            className={FILE}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || !ready}
          onClick={() => onImport({ internal, links })}
          className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {busy ? "Wird importiert …" : "Importieren"}
        </button>
        <button type="button" onClick={onCancel} className="h-11 rounded-md border border-field bg-white px-3.5 text-sm">
          Abbrechen
        </button>
      </div>
      {busy && (
        <p role="status" className="text-[13px] text-muted">
          Die Dateien werden gelesen und ausgewertet. Bei großen Crawls kann das einige Minuten dauern.
        </p>
      )}
    </div>
  );
}
